import React, { useMemo, useCallback } from 'react';
import { IHeaderParams } from 'ag-grid-community';
import {
    List,
    ListItem,
    Tooltip,
    LinearProgress,
    MenuItem,
    Select,
    Typography,
    Checkbox,
    Box,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { PreviewColumnT } from 'routes/Cellar/types';
import { SUPPORTED_TYPES } from './helpers/constants';
import { normalizeValue } from './helpers/dataParser';

interface ICustomHeaderParams extends IHeaderParams {
    columnType: DataTypesT;
    previewColumn: PreviewColumnT;
    nbRows: number;
    setColumns: DispatchState<PreviewColumnT[]>;
    isNewTable: boolean;
}

const StyTitleWrapper = styled('div', {
    name: 'StyTitleWrapper',
})({
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    alignItems: 'center',
    overflow: 'hidden',
});

const StyTitle = styled(Typography, {
    name: 'StyTitle',
    shouldForwardProp: (prop) => prop !== 'toImport',
})<{ toImport: boolean }>(({ toImport }) => ({
    width: '100%',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    textAlign: 'center',
    color: toImport ? 'black' : 'rgba(0, 0, 0, 0.38)',
}));

const StyPreviewHeaderElement = styled(Select, {
    name: 'StyPreviewHeaderElement',
})({
    alignSelf: 'center',
    margin: 0,
    minWidth: 80,
    '& .MuiSelect-select.MuiSelect-select': {
        padding: '1px 24px 1px 12px',
    },
});

const StyPreviewHeaderCheckbox = styled(Checkbox, {
    name: 'StyPreviewHeaderCheckbox',
})(({ theme }) => ({
    padding: 0,
    '&.Mui-checked': {
        color: theme.palette.primary.main,
    },
}));

const StyPreviewProgress = styled(LinearProgress, {
    name: 'StyPreviewProgress',
})(({ theme }) => ({
    marginTop: 5,
    '& .MuiLinearProgress-dashed': {
        animation: 'none',
    },
    '& .MuiLinearProgress-barColorPrimary': {
        backgroundColor: theme.palette.green,
    },
    '& .MuiLinearProgress-bar2Buffer': {
        backgroundColor: theme.palette.red,
    },
    '& .MuiLinearProgress-dashedColorPrimary': {
        backgroundImage: 'none',
        backgroundColor: theme.palette.lightGrey,
    },
}));

const PreviewHeader = (props: ICustomHeaderParams) => {
    const {
        columnType,
        previewColumn,
        nbRows,
        setColumns,
        displayName,
        isNewTable,
    } = props;
    const { types, toImport } = previewColumn;

    const onTypeChange = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            const newType = event.target.value as DataTypesT;
            setColumns((previousColumns) =>
                previousColumns.map((prevCol) =>
                    prevCol.name === previewColumn.name
                        ? { ...prevCol, type: newType }
                        : prevCol,
                ),
            );
        },
        [previewColumn, setColumns],
    );

    const onToImportChange = useCallback(
        () =>
            setColumns((previousColumns) =>
                previousColumns.map((prevCol) =>
                    prevCol.name === previewColumn.name
                        ? {
                              ...prevCol,
                              toImport: !previewColumn.toImport,
                          }
                        : prevCol,
                ),
            ),
        [previewColumn, setColumns],
    );

    const onBoxClick = useCallback(() => {
        if (!toImport) {
            onToImportChange();
        }
    }, [onToImportChange, toImport]);

    const { t } = useTranslation();

    const availableTypesOptions = Object.keys(types) as string[];

    // Compute formatted data indicator
    const formattableDataCount = types[columnType] || 0;
    const formattableDataRate = normalizeValue(
        formattableDataCount,
        nbRows || 1,
    );
    const unformattableDataRate = types.text
        ? normalizeValue(types.text - formattableDataCount, nbRows || 1)
        : 0;
    // TODO StrictNullCheck : Try to remove "!"
    const emptyDataRate = normalizeValue(
        nbRows - types.text! || 0,
        nbRows || 1,
    );

    const formattedDataTooltip = useCallback(
        (text: string, rate: number) => (
            <ListItem>
                {text} : <b>{rate}</b>%
            </ListItem>
        ),
        [],
    );
    const tooltipTitle = useMemo(
        () => (
            <div>
                <Typography variant="caption">Part de données:</Typography>
                <List>
                    {formattedDataTooltip(
                        t('cellar.csvPreview.properlyFormatted'),
                        Math.round(formattableDataRate),
                    )}
                    {formattedDataTooltip(
                        t('cellar.csvPreview.badlyFormatted'),
                        Math.round(unformattableDataRate),
                    )}
                    {formattedDataTooltip(
                        t('cellar.csvPreview.empty'),
                        Math.round(emptyDataRate),
                    )}
                </List>
            </div>
        ),
        [emptyDataRate, formattableDataRate, unformattableDataRate],
    );
    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
            }}
        >
            <Box sx={{ display: 'flex' }} onClick={onBoxClick}>
                {isNewTable && (
                    <Tooltip
                        placement="bottom"
                        title={t(
                            toImport
                                ? 'importCSV.deactivateColumn'
                                : 'importCSV.activateColumn',
                        )}
                    >
                        <StyPreviewHeaderCheckbox
                            checked={toImport}
                            onChange={onToImportChange}
                            size="small"
                        />
                    </Tooltip>
                )}
                <StyTitleWrapper>
                    <StyTitle toImport={toImport} variant="body1SemiBold">
                        {displayName}
                    </StyTitle>
                    <StyPreviewHeaderElement
                        variant="standard"
                        disabled={!isNewTable || !toImport}
                        value={columnType}
                        onChange={onTypeChange}
                        inputProps={{
                            name: 'Type',
                            id: 'type-select',
                        }}
                    >
                        {SUPPORTED_TYPES.map((type) => {
                            const isAvailable =
                                availableTypesOptions.includes(type);
                            return (
                                <MenuItem
                                    disabled={!isAvailable}
                                    key={type}
                                    value={type}
                                >
                                    <Typography variant="caption">
                                        {t(`cellar.csvInferedTypes.${type}`)}
                                    </Typography>
                                </MenuItem>
                            );
                        })}
                    </StyPreviewHeaderElement>
                </StyTitleWrapper>
            </Box>
            <Tooltip placement="bottom" title={tooltipTitle}>
                <StyPreviewProgress
                    variant="buffer"
                    color="primary"
                    value={formattableDataRate}
                    valueBuffer={formattableDataRate + unformattableDataRate}
                />
            </Tooltip>
        </Box>
    );
};

export default React.memo(PreviewHeader);
